import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  FileText,
  Mail,
  Download,
  MoreVertical,
  Brain,
  TrendingUp,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { analyzeSalaryTrends, generatePayslipSummary } from '../../lib/ai/payrollAssistant';

interface Payslip {
  id: string;
  employeeId: string;
  employeeName: string;
  period: string;
  grossPay: number;
  netPay: number;
  status: 'draft' | 'sent' | 'processed';
  deductions: {
    tax: number;
    insurance: number;
    retirement: number;
  };
  benefits: {
    health: number;
    dental: number;
    vision: number;
  };
  overtime: {
    hours: number;
    rate: number;
    amount: number;
  };
}

const mockPayslips: Payslip[] = [
  {
    id: '1',
    employeeId: 'EMP001',
    employeeName: 'John Doe',
    period: '2024-03-01',
    grossPay: 5000.0,
    netPay: 3850.0,
    status: 'processed',
    deductions: {
      tax: 750.0,
      insurance: 200.0,
      retirement: 200.0,
    },
    benefits: {
      health: 400.0,
      dental: 50.0,
      vision: 30.0,
    },
    overtime: {
      hours: 10,
      rate: 45.0,
      amount: 450.0,
    },
  },
  {
    id: '2',
    employeeId: 'EMP002',
    employeeName: 'Jane Smith',
    period: '2024-03-01',
    grossPay: 4500.0,
    netPay: 3462.5,
    status: 'draft',
    deductions: {
      tax: 675.0,
      insurance: 180.0,
      retirement: 182.5,
    },
    benefits: {
      health: 400.0,
      dental: 50.0,
      vision: 30.0,
    },
    overtime: {
      hours: 5,
      rate: 40.0,
      amount: 200.0,
    },
  },
];

export function PayslipList() {
  const [selectedPayslip, setSelectedPayslip] = useState<Payslip | null>(null);
  const [aiInsights, setAiInsights] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateInsights = async (payslip: Payslip) => {
    setLoading(true);
    setError(null);
    try {
      const [trendsInsight, payslipSummary] = await Promise.all([
        analyzeSalaryTrends([payslip]),
        generatePayslipSummary(payslip),
      ]);

      setAiInsights(`${payslipSummary}\n\nTrends Analysis:\n${trendsInsight}`);
      setSelectedPayslip(payslip);
    } catch (err) {
      setError('Failed to generate insights');
      console.error('Error generating payslip insights:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-dark-card rounded-lg shadow">
      <div className="p-6 border-b border-dark-border">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-dark-text">Payslips</h2>
          <button className="px-4 py-2 bg-primary-600 text-dark-text rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Generate Payslips
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-dark-bg">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                Employee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                Period
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                Gross Pay
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                Net Pay
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-dark-card divide-y divide-dark-border">
            {mockPayslips.map(payslip => (
              <tr key={payslip.id} className="hover:bg-dark-bg transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div>
                      <div className="text-sm font-medium text-dark-text">
                        {payslip.employeeName}
                      </div>
                      <div className="text-sm text-dark-muted">{payslip.employeeId}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-muted">
                  {format(new Date(payslip.period), 'MMMM yyyy')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-muted">
                  ${payslip.grossPay.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-muted">
                  ${payslip.netPay.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                    ${payslip.status === 'processed' ? 'bg-green-100 text-green-800' : ''}
                    ${payslip.status === 'sent' ? 'bg-blue-100 text-blue-800' : ''}
                    ${payslip.status === 'draft' ? 'bg-gray-100 text-gray-800' : ''}
                  `}
                  >
                    {payslip.status.charAt(0).toUpperCase() + payslip.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => generateInsights(payslip)}
                      className="text-primary-400 hover:text-primary-300"
                    >
                      <Brain className="h-4 w-4" />
                    </button>
                    <button className="text-dark-muted hover:text-dark-text">
                      <Mail className="h-4 w-4" />
                    </button>
                    <button className="text-dark-muted hover:text-dark-text">
                      <Download className="h-4 w-4" />
                    </button>
                    <button className="text-dark-muted hover:text-dark-text">
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedPayslip && (
        <div className="p-6 border-t border-dark-border">
          <div className="bg-dark-bg rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-dark-text flex items-center gap-2">
                <Brain className="h-5 w-5 text-primary-400" />
                AI Insights for {selectedPayslip.employeeName}
              </h3>
              {loading && <Loader2 className="h-5 w-5 animate-spin text-primary-400" />}
            </div>

            {error ? (
              <div className="flex items-center gap-2 text-red-400">
                <AlertCircle className="h-5 w-5" />
                <p>{error}</p>
              </div>
            ) : (
              <div className="prose prose-invert max-w-none">
                <p className="text-dark-muted whitespace-pre-line">{aiInsights}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
